import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
   renameProgressStage,
   renameProgressText,
   type RenameStage,
} from '@/utils/renameProgress';

/**
 * Live banner under the WiFi-name field: saved → router picked it up →
 * renaming → done (or a plain warning if the NodeMCU never came back —
 * the silent-forever state this whole feature exists to kill).
 *
 * Purely derived from record timestamps, so it survives closing and
 * reopening the screen mid-rename. While a rename is in flight it re-renders
 * every few seconds and nudges `onPoll` (the host screen's refetch) every
 * ~10s so the stages advance without a manual refresh.
 *
 * 🔴 Measured 2026-09-11: the banner only appeared after a page RELOAD. It
 * read the clock once at mount and only ticked while a rename was already
 * showing, so a rename saved minutes later carried a timestamp AHEAD of that
 * stale clock, elapsed went negative, and the stage came back null. Two
 * fixes: the clock is re-read whenever the rename anchor changes, and the
 * host passes `savedAt` (its own Save instant) so the banner shows at once,
 * before any refetch has landed — the record's own stamp takes over as soon
 * as it reflects this save.
 */
const RECORD_REFLECTS_SAVE_MS = 60_000;

type Device = Parameters<typeof renameProgressStage>[0];

const SsidRenameProgress: React.FC<{
   device: Device;
   /** Host's own clock at the instant its Save succeeded (ms since epoch). */
   savedAt?: number | null;
   onPoll?: () => void;
}> = ({ device, savedAt, onPoll }) => {
   const recordAt = device.router?.ssidRenamedAt
      ? new Date(device.router.ssidRenamedAt).getTime()
      : 0;
   // Prefer the server's stamp once it reflects this save (it is on the same
   // clock as the router/coin-box beats); until then the local Save instant
   // stands in so the operator sees the banner immediately.
   const recordReflectsSave = !!savedAt && recordAt >= savedAt - RECORD_REFLECTS_SAVE_MS;
   const anchorAt = savedAt && !recordReflectsSave ? savedAt : recordAt;
   const effective: Device =
      anchorAt === recordAt
         ? device
         : { ...device, router: { ...device.router, ssidRenamedAt: new Date(anchorAt) } };

   const [now, setNow] = useState(() => Date.now());
   // A new anchor (fresh save landed) must be judged against a fresh clock,
   // never the one captured when the screen opened.
   useEffect(() => {
      setNow(Date.now());
   }, [anchorAt]);

   const stage: RenameStage = renameProgressStage(effective, Math.max(now, anchorAt));
   const active = stage === 'router-wait' || stage === 'applying';

   useEffect(() => {
      if (!active) return;
      const tick = setInterval(() => setNow(Date.now()), 4000);
      return () => clearInterval(tick);
   }, [active]);

   // Hosts pass an inline arrow; keep the latest in a ref so the poll
   // interval is not torn down and restarted on every parent render.
   const pollRef = useRef(onPoll);
   pollRef.current = onPoll;
   useEffect(() => {
      if (!active) return;
      const poll = setInterval(() => pollRef.current?.(), 10_000);
      return () => clearInterval(poll);
   }, [active]);

   if (!stage) return null;
   const hasCoinBox = !!device.nodeMcu?.mac;
   return (
      <Banner data-stage={stage} $stage={stage}>
         {stage === 'done' ? '✅' : stage === 'stuck' ? '⚠️' : '⏳'}{' '}
         {renameProgressText(stage, hasCoinBox)}
      </Banner>
   );
};

const Banner = styled.div<{ $stage: string }>`
   font-size: 12px;
   line-height: 1.5;
   border-radius: 8px;
   padding: 8px 12px;
   margin-top: 8px;
   ${({ $stage }) =>
      $stage === 'done'
         ? 'color:#1a6b2f;background:#eaf7ee;border:1px solid #bfe3c8;'
         : $stage === 'stuck'
           ? 'color:#8a2a1a;background:#fdecea;border:1px solid #f2c4bc;'
           : 'color:#8a6d1a;background:#fdf6e3;border:1px solid #f0e0b0;'}
`;

export default SsidRenameProgress;
